// DeviceDetailsDialog – full device dossier in a modal (replaces inline expanded row).

import React, { JSX, useEffect } from 'react';
import { X } from 'lucide-react';
import { PortaledLayer } from '../../shared/PortaledLayer';
import DeviceDetails from '../../Device/DeviceDetails';
import type { DeviceMasterRow, DeviceStatus } from '../../../../types/DeviceListTypes';
import type { CommandDdbItem } from '../../../../types/Commands';

import type { DeviceDetailsDialogProps } from '../types/DeviceListViewProps';

export type { DeviceDetailsDialogProps };

export function DeviceDetailsDialog(props: DeviceDetailsDialogProps): JSX.Element | null {
    const {
        open,
        onClose,
        deviceId,
        row,
        columns,
        operatorScopedEventId,
        userIdToDisplayName = {},
        status,
        latestCommand,
        onRunDeviceValidation,
    } = props;

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open || !deviceId || !row) return null;

    return (
        <PortaledLayer>
            <div
                className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4 dark:bg-black/70"
                role="dialog"
                aria-modal="true"
                aria-labelledby="device-details-dialog-title"
                onClick={onClose}
            >
            <div
                className="flex max-h-[min(75vh,720px)] w-full max-w-[min(1400px,96vw)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800">
                    <h2 id="device-details-dialog-title" className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                        Device details
                        <span className="ml-2 font-normal text-gray-500 dark:text-slate-400">{deviceId}</span>
                    </h2>
                    <button
                        type="button"
                        className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" aria-hidden />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-4 py-3 dark:bg-slate-900">
                    <DeviceDetails
                        meta={row}
                        metaColumns={columns}
                        operatorScopedEventId={operatorScopedEventId}
                        userIdToDisplayName={userIdToDisplayName}
                        status={status}
                        latestCommand={latestCommand}
                        onRunDeviceValidation={onRunDeviceValidation}
                    />
                </div>
            </div>
            </div>
        </PortaledLayer>
    );
}
